import { useEffect, useState } from "react";
import { Mic, Square } from "lucide-react";
import { Button } from "@/ui/button";
import {
  FormCard,
  FormStackedRow,
  InlineMessage,
  SettingsHeader,
} from "@/components/settings/settings-ui";
import { formatRelativeTime } from "@/utils";
import {
  useDeviceMutation,
  useDevicesQuery,
  type Device,
} from "@/data/devices";

const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

function isOnline(device: Device, now: number): boolean {
  return (
    device.last_seen_at != null &&
    now - device.last_seen_at < ONLINE_THRESHOLD_MS
  );
}

interface DevicesSectionProps {
  workspaceSlug?: string | null;
}

export function DevicesSection({ workspaceSlug }: DevicesSectionProps) {
  const devicesQuery = useDevicesQuery(workspaceSlug);
  const deviceMutations = useDeviceMutation(workspaceSlug);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busyDeviceId, setBusyDeviceId] = useState<string | null>(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const devices = devicesQuery.data ?? [];

  const updateCaptureSettings = async (
    device: Device,
    settings: Partial<
      Pick<Device, "ambient_capture_enabled" | "voice_activation_enabled">
    >,
  ) => {
    setBusyDeviceId(device.id);
    setError(null);
    setSuccess(null);
    try {
      await deviceMutations.updateCaptureSettings.mutateAsync({
        deviceId: device.id,
        settings,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Settings update failed");
    } finally {
      setBusyDeviceId(null);
    }
  };

  const startExplicitCapture = async (device: Device) => {
    setBusyDeviceId(device.id);
    setError(null);
    setSuccess(null);
    try {
      await deviceMutations.startExplicitCapture.mutateAsync(device.id);
      setSuccess(`${device.name} is starting a manual recording.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Recording start failed");
    } finally {
      setBusyDeviceId(null);
    }
  };

  const stopExplicitCapture = async (device: Device) => {
    setBusyDeviceId(device.id);
    setError(null);
    setSuccess(null);
    try {
      await deviceMutations.stopExplicitCapture.mutateAsync({
        deviceId: device.id,
        captureSessionId: device.active_capture_session_id,
      });
      setSuccess(`${device.name} is stopping its manual recording.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Recording stop failed");
    } finally {
      setBusyDeviceId(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <SettingsHeader
        title="Devices"
        description="Parable ambient-capture devices registered to this workspace."
      />

      <div className="space-y-6">
        {error ? <InlineMessage variant="error">{error}</InlineMessage> : null}
        {success ? (
          <InlineMessage variant="success">{success}</InlineMessage>
        ) : null}

        <FormCard>
          <FormStackedRow
            label="Linked devices"
            hint={
              devicesQuery.isPending
                ? "Loading workspace devices."
                : devices.length === 0
                  ? "No workspace devices registered yet."
                  : `${devices.length} workspace device${devices.length === 1 ? "" : "s"} registered.`
            }
          >
            {devices.length > 0 ? (
              <ul className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
                {devices.map((d) => {
                  const online = isOnline(d, now);
                  const explicitActive =
                    d.active_capture_session_id != null &&
                    d.active_capture_mode === 2;
                  const busy = busyDeviceId === d.id;
                  return (
                    <li
                      key={d.id}
                      className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              online
                                ? "bg-emerald-400"
                                : "bg-muted-foreground/40"
                            }`}
                            aria-hidden
                          />
                          <span className="truncate text-[13px] font-medium text-foreground">
                            {d.name}
                          </span>
                          <span className="text-[12px] text-muted-foreground">
                            {online
                              ? "online"
                              : d.last_seen_at
                                ? `seen ${formatRelativeTime(d.last_seen_at)}`
                                : "never seen"}
                          </span>
                        </div>
                        <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                          {d.id}
                        </div>
                        {explicitActive && d.active_capture_started_at ? (
                          <div className="mt-1 text-[12px] text-muted-foreground">
                            recording since{" "}
                            {formatRelativeTime(d.active_capture_started_at)}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <CaptureSwitch
                          label="Ambient"
                          checked={d.ambient_capture_enabled}
                          disabled={busy || !online}
                          onChange={(checked) =>
                            void updateCaptureSettings(d, {
                              ambient_capture_enabled: checked,
                            })
                          }
                        />
                        <Button
                          variant={explicitActive ? "destructive" : "default"}
                          size="sm"
                          onClick={() =>
                            explicitActive
                              ? void stopExplicitCapture(d)
                              : void startExplicitCapture(d)
                          }
                          disabled={busy || !online}
                        >
                          {explicitActive ? (
                            <Square aria-hidden="true" />
                          ) : (
                            <Mic aria-hidden="true" />
                          )}
                          {explicitActive ? "Stop" : "Record"}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </FormStackedRow>
        </FormCard>
      </div>
    </div>
  );
}

function CaptureSwitch({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="inline-flex h-7 items-center gap-2 rounded-md border border-border bg-background px-2 text-[12px] text-muted-foreground">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      {label}
    </label>
  );
}
